import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useTranslate from "../../hooks/useTranslate";
import LoungeActivityPanel from "./LoungeActivityPanel";
import { loungeService } from "../../services/loungeService";
import { setLoungeUnread } from "../../store/slices/loungeSlice";

// Lounge page head — frames 1a/1b/1d (desktop) and 1e/1l (mobile):
// title "Agent Lounge" + info button (popover "What is the Agent Lounge?"),
// sub line "Visible to agents only.", Activity button right.
//
// T8 wires the Activity data (frame 1o): the button carries the unread badge
// (from the shared loungeSlice, so the Sofa nav icon shows the SAME number);
// opening it loads the reply/mention list — desktop popover 380px, mobile
// bottom-sheet. "Mark all read" nulls the badge; an item click jumps to the
// comment on the detail page. Empty stays the 1p·3 caught-up state.

const useOutsideClose = (open, onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);
  return ref;
};

const ActivityBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  const label = count > 99 ? "99+" : String(count);
  return (
    <span
      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-[5px] rounded-full bg-[#2283FF] text-white text-[11px] font-bold leading-none"
      data-testid="lounge-activity-badge"
      data-no-translate="true"
    >
      {label}
    </span>
  );
};

const LoungeHeader = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const unreadCount = useSelector((state) => state.lounge?.unreadCount || 0);

  const [infoOpen, setInfoOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const infoRef = useOutsideClose(infoOpen, () => setInfoOpen(false));
  const activityRef = useOutsideClose(activityOpen, () => setActivityOpen(false));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Badge on first paint: fetch the unread count once so it shows before the
  // panel is ever opened. Keeps the button + Sofa nav badge in sync via the
  // slice. Aborted on unmount.
  useEffect(() => {
    const ctrl = new AbortController();
    loungeService
      .getActivity(1, ctrl.signal)
      .then((res) => {
        if (res.success) dispatch(setLoungeUnread(res.unreadCount));
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [dispatch]);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError(false);
    const res = await loungeService.getActivity(1);
    setLoading(false);
    if (res.success) {
      setItems(res.results);
      dispatch(setLoungeUnread(res.unreadCount));
    } else {
      setError(true);
    }
  }, [dispatch]);

  const openActivity = () => {
    const next = !activityOpen;
    setActivityOpen(next);
    if (next) loadActivity();
  };

  const markAllRead = async () => {
    const res = await loungeService.markActivityRead();
    if (res.success) {
      const now = new Date().toISOString();
      setItems((list) => list.map((it) => ({ ...it, read_at: it.read_at || now })));
      dispatch(setLoungeUnread(res.unreadCount));
    }
  };

  // Item click (1o): jump to the comment on the detail page, and mark just
  // this one read (contract §3 `ids`). Optimistic badge decrement, corrected
  // by the server's unread_count.
  const handleItemClick = (item) => {
    setActivityOpen(false);
    if (!item.read_at) {
      dispatch(setLoungeUnread(Math.max(0, unreadCount - 1)));
      loungeService.markActivityRead([item.id]).then((res) => {
        if (res.success) dispatch(setLoungeUnread(res.unreadCount));
      });
      const now = new Date().toISOString();
      setItems((list) =>
        list.map((it) => (it.id === item.id ? { ...it, read_at: now } : it)),
      );
    }
    const hash = item.comment_id ? `#lounge-comment-${item.comment_id}` : "";
    navigate(`/lounge/${item.post_id}${hash}`);
  };

  const panelProps = {
    items,
    loading,
    error,
    unreadCount,
    onMarkAllRead: markAllRead,
    onItemClick: handleItemClick,
  };

  return (
    <div className="flex items-start gap-2.5 sm:gap-3">
      <div className="flex-1 min-w-0">
        <div ref={infoRef} className="relative flex items-center gap-1.5 sm:gap-[7px]">
          <h1 className="m-0 text-[18px] min-[402px]:text-[19px] sm:text-[21px] font-bold text-[#111827] tracking-[-0.2px]">
            {t("Agent Lounge")}
          </h1>
          <button
            type="button"
            aria-label={t("About the Agent Lounge")}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((o) => !o)}
            className={[
              // 18px visual dot, extended to a ≥44px touch target via the
              // invisible ::after box (mobile rule).
              "relative inline-flex items-center justify-center w-[18px] h-[18px] rounded-full border-[1.5px] text-[11px] font-bold leading-none p-0 shrink-0",
              "after:absolute after:-inset-3.5 after:content-['']",
              infoOpen
                ? "border-[#2283FF] bg-[#EDF4FF] text-[#2283FF]"
                : "border-[#9CA3AF] bg-transparent text-[#9CA3AF] hover:border-[#2283FF] hover:text-[#2283FF]",
            ].join(" ")}
            style={{ fontFamily: "Georgia, serif" }}
            data-no-translate="true"
          >
            i
          </button>
          {infoOpen && (
            <div className="absolute top-7 left-0 z-20 w-[min(350px,calc(100vw-32px))] bg-white border border-[#E4E9F0] rounded-xl shadow-[0_12px_32px_rgba(17,24,39,0.14)] py-3.5 px-4">
              <p className="m-0 text-[13.5px] font-bold text-[#111827]">
                {t("What is the Agent Lounge?")}
              </p>
              <p className="m-0 mt-1.5 text-[13px] leading-[1.55] text-[#6B7280]">
                {t(
                  "The agents-only corner of TransferPitch: a feed where agents swap craft on regulations, commissions and window strategy, with peers instead of the public. Posts never leave the lounge.",
                )}
              </p>
              <p className="m-0 mt-2 text-[12.5px] text-[#111827]">
                <span className="font-semibold">{t("House rule:")}</span>{" "}
                {t("craft yes, names no.")}
              </p>
            </div>
          )}
        </div>
        <p className="m-0 mt-[3px] sm:mt-1 text-[12px] min-[402px]:text-[12.5px] sm:text-[13px] text-[#9CA3AF]">
          {t("Visible to agents only.")}
        </p>
      </div>

      <div ref={activityRef} className="relative shrink-0">
        <button
          type="button"
          aria-expanded={activityOpen}
          onClick={openActivity}
          className={[
            "inline-flex items-center gap-1.5 min-[402px]:gap-[7px] sm:gap-2 bg-white border rounded-[10px] h-11 sm:h-auto px-[11px] min-[402px]:px-[13px] sm:py-2 sm:px-3.5 text-[12.5px] min-[402px]:text-[13px] sm:text-[13.5px] font-semibold text-[#111827] whitespace-nowrap",
            activityOpen
              ? "border-[#2283FF] shadow-[0_0_0_3px_rgba(34,131,255,0.14)]"
              : "border-[#DDE3EB] hover:border-[#C6CFDA]",
          ].join(" ")}
        >
          {t("Activity")}
          <ActivityBadge count={unreadCount} />
        </button>

        {activityOpen && (
          <>
            {/* Desktop popover (1o) — 380px, anchored under the button. */}
            <div className="hidden sm:block absolute top-[calc(100%+8px)] right-0 z-20 w-[380px] max-w-[calc(100vw-32px)] bg-white border border-[#E4E9F0] rounded-[14px] shadow-[0_16px_40px_rgba(17,24,39,0.10)] overflow-hidden">
              <LoungeActivityPanel {...panelProps} variant="popover" />
            </div>

            {/* Mobile bottom-sheet (1o) — dim + sheet from the bottom edge. */}
            <div className="sm:hidden fixed inset-0 z-40" role="dialog" aria-modal="true">
              <div
                className="absolute inset-0 bg-[rgba(17,24,39,0.40)]"
                onClick={() => setActivityOpen(false)}
                aria-hidden="true"
              />
              <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-[0_-12px_40px_rgba(17,24,39,0.18)] pt-2.5 pb-3 max-h-[80vh] flex flex-col">
                <span className="w-10 h-1 rounded-full bg-[#DDE3EB] mx-auto shrink-0" />
                <LoungeActivityPanel {...panelProps} variant="sheet" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoungeHeader;
